'use client';

import { useCallback, useEffect, useRef } from 'react';
import { PublicKey } from '@solana/web3.js';
import { useCustomWalletAutoconnect } from 'src/hooks/useCustomWalletAutoconnect';
import useWalletContext from 'src/hooks/useWalletContext';
import { dlog } from '../dev';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import useCurrentWalletConnectionState from './useCurrentWalletConnectionState';
import useVelocityClient from './useVelocityClient';
import { useLinkWallet } from './wallet-management/useLinkWallet';
import useWalletDisconnect from './wallet-management/useWalletDisconnect';
import { LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY } from 'src/hooks/useWalletsToDisplay';
import { isPrivateBetaRejectedAuthority } from 'src/hooks/privateBetaWalletWhitelist/privateBetaWalletWhitelistState';

/**
 * A hook which handles making sure the app correctly reacts to the wallet changing state, keeps the store state in sync with the changing wallet, and attaches the wallet's events to the assosciated methods in the store
 */
const useKeepWalletsInSync = () => {
	const setState = useVelocityStore((s) => s.set);
	useCustomWalletAutoconnect();
	const walletContext = useWalletContext();
	const authority = walletContext?.wallet?.adapter?.publicKey;
	const velocityClient = useVelocityClient();
	const isEmulatingAccount = useVelocityStore((s) => s.isEmulatingAccount);
	const walletConnectionState = useCurrentWalletConnectionState();

	const { linkWalletToVelocityClient } = useLinkWallet();
	const { handleWalletDisconnect } = useWalletDisconnect();

	const wallets = walletContext.wallets;

	const currentWalletState = useVelocityStore((s) => s.wallet);

	const alreadySyncedAuthority = useRef<PublicKey>(null);
	const linkInProgress = useRef(false);

	const handleDisconnect = useCallback(() => {
		dlog(`wallet_debugging`, `useKeepWalletsInSync::handle_disconnect`);

		alreadySyncedAuthority.current = null;
		handleWalletDisconnect();
		walletConnectionState.instance.update('NotConnected', true);
	}, [handleWalletDisconnect, walletConnectionState.instance]);

	/**
	 * This method is designed to handle the race-condition where the wallet might want to connect before the RPC-Connection or VelocityClient are actually ready to go. It first checks if they are ready, otherwise sets up a handler to handle the wallet connection once they are ready to go.
	 */

	// Keep wallet adapters in sync
	useEffect(() => {
		setState((s) => {
			s.wallets = wallets;
		});
	}, [wallets]);

	// TODO :: The fact that we need to set these handlers up seperately is weird and annoying. I think the issue is that lots of our wallet code assumes the current wallet is inside of WalletContext when IT IS NOT - ONLY solana/react WALLETS are in here. So I think that metamask is also not behaving correctly until we standardise this.
	// Add Custom Wallet event handlers
	useEffect(() => {
		if (!currentWalletState?.current?.adapter) return;

		if (!currentWalletState.isAppKit && !currentWalletState.isPrivyNativeAuth) {
			return;
		}

		currentWalletState.current.adapter.on('disconnect', handleDisconnect);

		return () => {
			currentWalletState.current.adapter?.removeListener(
				'disconnect',
				handleDisconnect
			);
		};
	}, [currentWalletState, handleDisconnect]);

	// Set up solana/react wallet event handlers
	useEffect(() => {
		const wallet = walletContext?.wallet;

		if (!wallet) return;

		if (!isEmulatingAccount) {
			setState((s) => {
				// @ts-expect-error
				s.wallet.current = wallet;
			});
		}

		wallet.adapter.on('disconnect', handleDisconnect);

		return () => {
			wallet.adapter.removeListener('disconnect', handleDisconnect);
		};
	}, [
		walletContext?.wallet?.adapter,
		handleDisconnect,
		currentWalletState.current.adapter?.name,
		currentWalletState.isPrivyNativeAuth,
		isEmulatingAccount,
	]);

	// Ensure that wallet connection logic happens when the adapter connects
	useEffect(() => {
		// Exit if we're using account emulation
		if (isEmulatingAccount) {
			return;
		}

		if (authority) {
			if (isPrivateBetaRejectedAuthority(authority.toBase58())) {
				return;
			}

			// Skip doing connection logic if we've already connected for this wallet
			if (alreadySyncedAuthority?.current?.equals(authority)) {
				return;
			}

			if (velocityClient && !linkInProgress.current) {
				// Only mark this authority as synced once the link actually succeeds.
				// If linking bails (e.g. the adapter's publicKey isn't ready yet), we leave
				// the ref unset so a subsequent effect run can retry once the key resolves —
				// rather than marking it synced up-front and getting stuck unlinked.
				linkInProgress.current = true;
				linkWalletToVelocityClient()
					.then((linked) => {
						if (linked) {
							alreadySyncedAuthority.current = authority;
						}
					})
					.finally(() => {
						linkInProgress.current = false;
					});
			}

			if (currentWalletState.current.adapter) {
				localStorage.setItem(
					LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY,
					currentWalletState.current.adapter.name
				);
			}
		}
	}, [
		walletConnectionState,
		authority,
		isEmulatingAccount,
		velocityClient,
		linkWalletToVelocityClient,
		currentWalletState?.current?.adapter?.name,
	]);

	// Store wallet context on the window for e2e tests
	useEffect(() => {
		// @ts-ignore
		window.walletContext = walletContext;
	}, [walletContext]);
};

export default useKeepWalletsInSync;
